import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Input, Label } from 'reactstrap';
import { CommonModal, ButtonBuilder, ToastBuilder, ApiRequest } from '../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe } from '../../../../../../../../GlobalComponents/Helpers';

/**
 * TwoWaySyncBuilderModal — dedicated, cascading builder for creating a two-way
 * sync. Replaces the generic create form for the two-way-sync job type.
 *
 * The inputs are dependency-driven: each step unlocks the next.
 *   1. Services   — pick 1 or 2 services the sync moves data between (max 2).
 *   2. Direction  — name the first job; choose whether to also create the
 *                   complement (reverse-direction) job, and name it.
 *   3. First conn — the first job's SOURCE connection, limited to the chosen
 *                   service(s).
 *   4. Second conn — the first job's DESTINATION connection, excluding the first
 *                   connection and (when 2 services were chosen) restricted to
 *                   the OTHER service.
 *   5. Sandbox    — optional test connection.
 *
 * Data comes from GET /dmt/syncBuilder (partner-scoped services + connections).
 * Submit goes to POST /dmt/jobs (createDmtJob), which creates the pair + links
 * them. The legacy /tools/{toolId}/jobs route is untouched.
 *
 * Optionally, "seed from another sync" can be chosen; on success we redirect to
 * the new job with ?seedSync=1 so the Copy Sync Settings modal auto-opens.
 */
const TwoWaySyncBuilderModal = ({ toolData }) => {

    const toolId = toolData?.tool_id;
    const toolRoute = toolData?.route;

    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Selections
    const [selectedServiceIds, setSelectedServiceIds] = useState([]); // max 2
    const [name, setName] = useState('');
    const [createComplement, setCreateComplement] = useState('yes');
    const [complementName, setComplementName] = useState('');
    const [sourceConnection, setSourceConnection] = useState('');
    const [destinationConnection, setDestinationConnection] = useState('');

    const reset = () => {
        setSelectedServiceIds([]);
        setName('');
        setCreateComplement('yes');
        setComplementName('');
        setSourceConnection('');
        setDestinationConnection('');
    };

    const open = () => {
        setModal(true);
        reset();
        fetchSyncBuilder();
    };

    const fetchSyncBuilder = () => {
        setLoading(true);
        ApiRequest('GET', '/dmt/syncBuilder', {
            toastOnError: 'Failed to load services and connections',
        }).then((res) => {
            const raw = res.data?.records ?? res.data;
            const parsed = typeof raw === 'string' ? parseJSONSafe(raw) : raw;
            setServices(res.success && parsed?.success && Array.isArray(parsed.services) ? parsed.services : []);
            setLoading(false);
        });
    };

    // All connections across the chosen services (flat list).
    const connectionsForSelectedServices = useMemo(() => {
        const chosen = services.filter((s) => selectedServiceIds.includes(s.service_id));
        return chosen.flatMap((s) => s.connections || []);
    }, [services, selectedServiceIds]);

    // Lookup helpers.
    const connById = useMemo(() => {
        const map = {};
        connectionsForSelectedServices.forEach((c) => { map[String(c.id)] = c; });
        return map;
    }, [connectionsForSelectedServices]);

    // Source options = every connection on the chosen service(s).
    const sourceOptions = connectionsForSelectedServices;

    // Destination options cascade off the source selection:
    //   - always exclude the source connection itself
    //   - when 2 services were chosen, restrict to the OTHER service (so the two
    //     directions genuinely cross services). With 1 service, stay in-service.
    const destinationOptions = useMemo(() => {
        if (!sourceConnection) return [];
        const src = connById[String(sourceConnection)];
        if (!src) return [];
        return connectionsForSelectedServices.filter((c) => {
            if (String(c.id) === String(sourceConnection)) return false;
            if (selectedServiceIds.length === 2) return c.service_id !== src.service_id;
            return true;
        });
    }, [sourceConnection, connById, connectionsForSelectedServices, selectedServiceIds]);

    // Reset dependent selections when upstream choices change.
    useEffect(() => { setSourceConnection(''); setDestinationConnection(''); }, [selectedServiceIds]);
    useEffect(() => { setDestinationConnection(''); }, [sourceConnection]);

    const toggleService = (serviceId, checked) => {
        setSelectedServiceIds((prev) => {
            if (checked) {
                if (prev.includes(serviceId)) return prev;
                if (prev.length >= 2) {
                    ToastBuilder('error', 'A sync can use at most 2 services');
                    return prev;
                }
                return [...prev, serviceId];
            }
            return prev.filter((id) => id !== serviceId);
        });
    };

    const canSubmit = selectedServiceIds.length >= 1
        && name.trim()
        && sourceConnection
        && destinationConnection
        && (createComplement === 'no' || true);

    const handleSubmit = () => {
        if (selectedServiceIds.length < 1) return ToastBuilder('error', 'Select at least one service');
        if (!name.trim()) return ToastBuilder('error', 'Enter a job name');
        if (!sourceConnection) return ToastBuilder('error', 'Select the first connection');
        if (!destinationConnection) return ToastBuilder('error', 'Select the second connection');

        ApiRequest('POST', '/dmt/jobs', {
            body: {
                toolId,
                name: name.trim(),
                jobType: 'two-way-sync',
                sourceConnection: parseInt(sourceConnection),
                destinationConnection: parseInt(destinationConnection),
                createComplement,
                complementName: createComplement === 'yes' ? (complementName.trim() || `${name.trim()} - Sync`) : null,
            },
            setIsProcessing: setSubmitting,
            toastOnError: true,
        }).then((res) => {
            const data = res.data || {};
            if (res.success && data.id) {
                ToastBuilder('success', 'Sync Created Successfully');
                window.location.href = `${process.env.PUBLIC_URL}/tools/${toolRoute}/jobs/${String(data.id)}`;
            } else {
                ToastBuilder('error', 'Unable to create sync');
            }
        });
    };

    const twoServices = selectedServiceIds.length === 2;

    return (
        <Fragment>
            <ButtonBuilder label='Create Two-Way Sync' className='btn-secondary' color={null}
                style={{ maxWidth: 550 }} onClick={open} />

            <CommonModal isOpen={modal} title='Create Two-Way Sync' size='lg'
                toggler={() => { setModal(false); reset(); }}
                onSubmit={true}
                onSubmitLabel='Create Sync'
                customSubmit={handleSubmit}
                formIsValid={!!canSubmit}
                onCancelLabel='Close'>

                {loading ? (
                    <p style={{ textAlign: 'center', color: '#999', padding: 20 }}>Loading services...</p>
                ) : (
                    <Fragment>
                        {/* Step 1 — services */}
                        <div style={{ marginBottom: 18 }}>
                            <Label style={{ fontSize: 13, fontWeight: 600 }}>Services <span style={{ color: '#999', fontWeight: 400 }}>(pick 1 or 2)</span></Label>
                            {services.length === 0 ? (
                                <p style={{ color: '#999', fontSize: 12 }}>No connections available for this partner.</p>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                                    {services.map((s) => {
                                        const checked = selectedServiceIds.includes(s.service_id);
                                        const disabled = !checked && selectedServiceIds.length >= 2;
                                        return (
                                            <label key={s.service_id} className='d-flex align-items-center'
                                                style={{ gap: 10, margin: 0, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                                <Input type='checkbox' checked={checked} disabled={disabled}
                                                    onChange={(e) => toggleService(s.service_id, e.target.checked)}
                                                    style={{ marginTop: 0 }} />
                                                <span style={{ fontSize: 13 }}>
                                                    {s.service_label}
                                                    <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>
                                                        ({(s.connections || []).length} connection(s))
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Step 2 — name + complement (unlocked after >=1 service) */}
                        {selectedServiceIds.length >= 1 && (
                            <div style={{ marginBottom: 18 }}>
                                <Label style={{ fontSize: 13, fontWeight: 600 }}>Job Name</Label>
                                <Input bsSize='sm' value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder='e.g. CWM → FreshService' />

                                <div style={{ marginTop: 10 }}>
                                    <Label style={{ fontSize: 13, fontWeight: 600 }}>Complement Job</Label>
                                    <Input type='select' bsSize='sm' value={createComplement}
                                        onChange={(e) => setCreateComplement(e.target.value)}>
                                        <option value='yes'>Create both directions (recommended)</option>
                                        <option value='no'>Only this direction for now</option>
                                    </Input>
                                    {createComplement === 'yes' && (
                                        <Input bsSize='sm' value={complementName} style={{ marginTop: 6 }}
                                            onChange={(e) => setComplementName(e.target.value)}
                                            placeholder={name.trim() ? `${name.trim()} - Sync (default)` : 'Complement job name (optional)'} />
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Steps 3 & 4 — first + second connection, side by side.
                            Second is disabled until the first is chosen (cascading). */}
                        {selectedServiceIds.length >= 1 && name.trim() && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                                <div>
                                    <Label style={{ fontSize: 13, fontWeight: 600 }}>First Connection <span style={{ color: '#999', fontWeight: 400 }}>(source)</span></Label>
                                    <Input type='select' bsSize='sm' value={sourceConnection}
                                        onChange={(e) => setSourceConnection(e.target.value)}>
                                        <option value=''>Select a connection...</option>
                                        {sourceOptions.map((c) => (
                                            <option key={c.id} value={c.id}>{`${c.name} (${c.service_label})`}</option>
                                        ))}
                                    </Input>
                                </div>
                                <div>
                                    <Label style={{ fontSize: 13, fontWeight: 600 }}>
                                        Second Connection <span style={{ color: '#999', fontWeight: 400 }}>
                                            (destination{twoServices ? ' — other service' : ''})
                                        </span>
                                    </Label>
                                    <Input type='select' bsSize='sm' value={destinationConnection}
                                        disabled={!sourceConnection}
                                        onChange={(e) => setDestinationConnection(e.target.value)}>
                                        <option value=''>{sourceConnection ? 'Select a connection...' : 'Pick the first connection first'}</option>
                                        {destinationOptions.map((c) => (
                                            <option key={c.id} value={c.id}>{`${c.name} (${c.service_label})`}</option>
                                        ))}
                                    </Input>
                                    {sourceConnection && destinationOptions.length === 0 && (
                                        <p style={{ color: '#999', fontSize: 11, marginTop: 4 }}>
                                            No eligible second connection. {twoServices ? 'Add a connection on the other service.' : 'Add another connection on this service.'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 5 — seeding note (unlocked after both connections) */}
                        {sourceConnection && destinationConnection && (
                            <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                                To seed this job's resources and mappings from an existing sync, open it after
                                creating and use <strong>Job Actions → Setting Import</strong>.
                            </p>
                        )}
                    </Fragment>
                )}
            </CommonModal>
        </Fragment>
    );
};

export default TwoWaySyncBuilderModal;
